import { Capacitor } from '@capacitor/core';
import EposPlugin from 'capacitor-epos';

export interface BluetoothPermissionResult {
  granted: boolean;
  state: 'authorized' | 'denied' | 'restricted' | 'not_determined' | 'unsupported';
  message?: string;
}

/**
 * Request Bluetooth permission for iOS
 * Note: On iOS, Bluetooth permission is automatically requested when using Core Bluetooth APIs
 * This function returns the current permission state without triggering a discovery scan
 */
export async function requestBluetoothPermission(): Promise<BluetoothPermissionResult> {
  if (Capacitor.getPlatform() === 'web') {
    return {
      granted: false,
      state: 'unsupported',
      message: 'Bluetooth is not supported on web platform',
    };
  }

  if (Capacitor.getPlatform() !== 'ios') {
    // Android handles permissions through Capacitor's permission system
    return {
      granted: true,
      state: 'authorized',
    };
  }

  // On iOS, we can't directly check Bluetooth permission status
  // Permission will be requested automatically when the user triggers a scan
  // Return 'not_determined' to indicate the user should try scanning
  return {
    granted: false,
    state: 'not_determined',
    message: 'Bluetooth permission will be requested when you scan for printers.',
  };
}

/**
 * Check Bluetooth state on iOS
 * Note: This does NOT trigger a printer discovery scan
 * The actual permission will be requested when the user explicitly scans for printers
 */
export async function checkBluetoothState(): Promise<{
  enabled: boolean;
  authorized: boolean;
  state: string;
}> {
  if (Capacitor.getPlatform() === 'web') {
    return {
      enabled: false,
      authorized: false,
      state: 'unsupported',
    };
  }
  // Prefer native plugin check when available
  try {
    const hasFn = EposPlugin && typeof (EposPlugin as any).checkBluetoothState === 'function';
    if (hasFn) {
      const res: any = await (EposPlugin as any).checkBluetoothState();
      // plugin may return { supported, enabled } or { enabled, state }
      const enabled = !!(res && (res.enabled === true || res.supported === true && res.enabled !== false));
      const supported = res && typeof res.supported !== 'undefined' ? !!res.supported : true;
      const state = enabled ? (res.state || 'poweredOn') : (res.state || 'poweredOff');
      return { enabled, authorized: supported && enabled, state };
    }
  } catch (err: any) {
    const msg = (err?.message || '').toLowerCase();
    if (msg.includes('poweredoff') || msg.includes('turned off') || msg.includes('off')) {
      return { enabled: false, authorized: false, state: 'poweredOff' };
    }
    if (msg.includes('permission') || msg.includes('unauthorized') || msg.includes('denied')) {
      return { enabled: false, authorized: false, state: 'denied' };
    }
    // fallthrough to conservative defaults
  }

  // Fallback behavior when plugin not available:
  if (Capacitor.getPlatform() !== 'ios') {
    return {
      enabled: true,
      authorized: true,
      state: 'poweredOn',
    };
  }

  return {
    enabled: true,
    authorized: true,
    state: 'unknown',
  };
}
